import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveAccrued } from '../../hooks/contract/useLivePending';
import { useClaim, useReinvest, useReinvestFull, useWithdraw } from '../../hooks/contract/useWriteActions';
import {
  formatUsdt,
  formatDuration,
  progressPct,
  matureAt,
  isMatured,
  planDurationSec,
  formatPlanLabel,
} from '../../lib/format';
import { MIN_STAKE_WEI, PROTOCOL_FEE_BPS, BPS_DENOM } from '../../lib/constants';

const DepositRow = ({ deposit, depositIdx, plan, plans }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const accrued = useLiveAccrued(deposit, plan);

  useEffect(() => {
    if (!deposit.active) return undefined;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 30_000);
    return () => clearInterval(id);
  }, [deposit.active]);
  const [showReinvest, setShowReinvest] = useState(false);
  const [targetPlan, setTargetPlan] = useState(Number(deposit.planId));

  const claimAction = useClaim();
  const reinvestAction = useReinvest();
  const reinvestFullAction = useReinvestFull();
  const withdrawAction = useWithdraw();

  const matured = plan
    ? isMatured({
        startedAt: deposit.startedAt,
        durationSec: planDurationSec(plan),
        now,
      })
    : false;

  const endsAt = plan ? matureAt({ startedAt: deposit.startedAt, durationSec: planDurationSec(plan) }) : 0;
  const progress = plan
    ? progressPct({ startedAt: deposit.startedAt, durationSec: planDurationSec(plan), now })
    : 0;
  const remaining = Math.max(0, endsAt - now);

  const fee = (accrued * BigInt(PROTOCOL_FEE_BPS)) / BigInt(BPS_DENOM);
  const netAfterFee = accrued - fee;

  const txBusy =
    claimAction.status === 'signing' ||
    claimAction.status === 'confirming' ||
    reinvestAction.status === 'signing' ||
    reinvestAction.status === 'confirming' ||
    reinvestFullAction.status === 'signing' ||
    reinvestFullAction.status === 'confirming' ||
    withdrawAction.status === 'signing' ||
    withdrawAction.status === 'confirming';

  const principalPlusNet = BigInt(deposit.amount) + netAfterFee;
  const canReinvestActive = netAfterFee >= MIN_STAKE_WEI;
  const canReinvestFull = principalPlusNet >= MIN_STAKE_WEI;
  const canReinvest = matured ? canReinvestFull : canReinvestActive;

  if (!deposit.active) {
    return (
      <div className="dep-row dep-row--closed">
        <div className="dep-row__head">
          <span className="dep-row__num mono">#{depositIdx}</span>
          <span className="dep-row__status dep-row__status--closed">
            {t('dashboard.deposits.status.closed')}
          </span>
        </div>
        <div className="dep-row__amt mono">{formatUsdt(deposit.amount)} USDT</div>
        <div className="dep-row__plan">
          {plan
            ? formatPlanLabel(plan, t)
            : t('dashboard.common.planShort', { id: String(deposit.planId) })}
        </div>
      </div>
    );
  }

  return (
    <div className={`dep-row dep-row--active${matured ? ' dep-row--matured' : ''}`}>
      <div className="dep-row__head">
        <span className="dep-row__num mono">#{depositIdx}</span>
        <span
          className={`dep-row__status ${
            matured ? 'dep-row__status--matured' : 'dep-row__status--active'
          }`}
        >
          {matured ? t('dashboard.deposits.status.matured') : t('dashboard.deposits.status.active')}
        </span>
      </div>

      <div className="dep-row__main">
        <div className="dep-row__amt mono tabular-nums">{formatUsdt(deposit.amount)} USDT</div>
        <div className="dep-row__plan">
          {plan
            ? formatPlanLabel(plan, t)
            : t('dashboard.common.planShort', { id: String(deposit.planId) })}
        </div>
      </div>

      <div className="dep-row__progress">
        <div className="dep-row__progress-bar" style={{ width: `${progress.toFixed(1)}%` }} />
        <div className="dep-row__progress-label">
          {matured
            ? t('dashboard.deposits.progress.ready')
            : t('dashboard.deposits.progress.remaining', { time: formatDuration(remaining) })}
        </div>
      </div>

      <div className="dep-row__pending">
        <div className="dep-row__pending-label">{t('dashboard.deposits.pending.label')}</div>
        <div className="dep-row__pending-value mono tabular-nums">
          {formatUsdt(accrued, { maximumFractionDigits: 6, minimumFractionDigits: 6 })} USDT
        </div>
        <div className="dep-row__pending-net">
          {t('dashboard.deposits.pending.net', {
            value: formatUsdt(netAfterFee, { maximumFractionDigits: 4, minimumFractionDigits: 2 }),
          })}
        </div>
      </div>

      <div className="dep-row__actions">
        {matured ? (
          <button
            type="button"
            className="sf-btn sf-btn--gold sf-btn--sm"
            onClick={() => withdrawAction.withdraw(depositIdx)}
            disabled={txBusy}
            title={t('dashboard.deposits.btn.claimFullHint')}
          >
            <i className="fas fa-sack-dollar" /> {t('dashboard.deposits.btn.claimFull')}
          </button>
        ) : (
          <button
            type="button"
            className="sf-btn sf-btn--gold sf-btn--sm"
            onClick={() => claimAction.claim(depositIdx)}
            disabled={txBusy || accrued === 0n}
          >
            <i className="fas fa-coins" /> {t('dashboard.deposits.btn.claim')}
          </button>
        )}

        <div className="dep-row__reinvest">
          <button
            type="button"
            className="sf-btn sf-btn--outline sf-btn--sm"
            onClick={() => setShowReinvest((v) => !v)}
            disabled={txBusy || !canReinvest}
            title={
              !canReinvest
                ? t('dashboard.deposits.btn.reinvestTooLow')
                : matured
                  ? t('dashboard.deposits.btn.reinvestFullHint')
                  : t('dashboard.deposits.btn.reinvestActiveHint')
            }
          >
            <i className="fas fa-recycle" />{' '}
            {matured
              ? t('dashboard.deposits.btn.reinvestFull')
              : t('dashboard.deposits.btn.reinvest')}
            <i className={`fas fa-chevron-${showReinvest ? 'up' : 'down'}`} />
          </button>
          {showReinvest && (
            <div className="dep-row__reinvest-panel">
              <div className="dep-row__reinvest-amount mono tabular-nums">
                {matured
                  ? t('dashboard.deposits.btn.reinvestFullAmount', {
                      value: formatUsdt(principalPlusNet),
                    })
                  : t('dashboard.deposits.btn.reinvestActiveAmount', {
                      value: formatUsdt(netAfterFee),
                    })}
              </div>
              <label className="dep-row__reinvest-label">
                {t('dashboard.deposits.btn.reinvestTarget')}
              </label>
              <select
                className="dep-row__reinvest-select mono"
                value={targetPlan}
                onChange={(e) => setTargetPlan(Number(e.target.value))}
              >
                {plans.map((p, i) => (
                  <option key={i} value={i}>
                    {formatPlanLabel(p, t)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="sf-btn sf-btn--gold sf-btn--sm"
                onClick={async () => {
                  const res = matured
                    ? await reinvestFullAction.reinvestFull(depositIdx, targetPlan)
                    : await reinvestAction.reinvest(depositIdx, targetPlan);
                  if (res.success) setShowReinvest(false);
                }}
                disabled={txBusy}
              >
                {matured
                  ? t('dashboard.deposits.btn.reinvestFullConfirm')
                  : t('dashboard.deposits.btn.reinvestConfirm')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepositRow;
